import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';
import { 
  Users, Plus, Edit, Trash2, Phone, Mail, Shield, 
  UserCog, Calendar
} from 'lucide-react';
import ViewOnlyBanner from '../../components/ViewOnlyBanner';
import { isViewOnly, isAdmin } from '../../utils/adminRole';

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'staff',
    password_hash: '',
    active: true
  });

  useEffect(() => {
    loadStaff();
    // Check if current user can edit staff
    setIsReadOnly(isViewOnly('staff'));
  }, []);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getStaff();
      setStaff(data);
    } catch (error) {
      toast.error('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'staff',
      password_hash: '',
      active: true
    });
    setEditingStaff(null);
  };

  const handleEditStaff = (member) => {
    setEditingStaff(member);
    setFormData({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      role: member.role || 'staff',
      password_hash: '', // Don't show existing password
      active: member.active !== false
    });
    setIsDialogOpen(true);
  };

  const handleSaveStaff = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      const dataToSend = { ...formData };
      
      // Only include password if it's set (for new staff or password change)
      if (!dataToSend.password_hash) {
        delete dataToSend.password_hash;
      }

      if (editingStaff) {
        await adminAPI.updateStaff(editingStaff.id, dataToSend);
        toast.success('Staff member updated');
      } else {
        if (!formData.password_hash) {
          toast.error('Password is required for new staff');
          return;
        }
        await adminAPI.createStaff(dataToSend);
        toast.success('Staff member created');
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save staff member');
    }
  };

  const handleDeleteStaff = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to delete ${staffName}?`)) {
      return;
    }

    try {
      await adminAPI.deleteStaff(staffId);
      toast.success('Staff member deleted');
      loadStaff();
    } catch (error) {
      toast.error('Failed to delete staff member');
    }
  };

  const handleDialogClose = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-700';
      case 'manager':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4" />;
      case 'manager':
        return <UserCog className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  return (
    <div data-testid="staff-management-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl font-black font-heading uppercase">Staff Management</h1>
        <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="btn-volt" onClick={resetForm} data-testid="add-staff-button">
              <Plus className="w-5 h-5 mr-2" />
              Add Staff
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">
                {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {editingStaff
                  ? 'Update staff member details, role and active status.'
                  : 'Add a new staff member with role, contact info and password.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
                  Full Name *
                </Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  className="h-12 rounded-2xl"
                  data-testid="staff-name-input"
                />
              </div>

              <div>
                <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
                  Email Address *
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@smashzone.com"
                  className="h-12 rounded-2xl"
                  data-testid="staff-email-input"
                />
              </div>

              <div>
                <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
                  Mobile Number
                </Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+673 XXX XXXX"
                  className="h-12 rounded-2xl"
                  data-testid="staff-phone-input"
                />
              </div>

              <div>
                <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
                  Role {isAdmin() ? '*' : ''}
                </Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, role: val }))}
                  disabled={!isAdmin()} // Only admins can change role
                >
                  <SelectTrigger className="h-12 rounded-2xl" data-testid="staff-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
                {!isAdmin() && (
                  <p className="text-xs text-muted-foreground mt-1">Only administrators can change roles</p>
                )}
              </div>

              <div>
                <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
                  {editingStaff ? 'New Password (leave blank to keep current)' : 'Password *'}
                </Label>
                <Input
                  type="password"
                  value={formData.password_hash}
                  onChange={(e) => setFormData(prev => ({ ...prev, password_hash: e.target.value }))}
                  placeholder={editingStaff ? '••••••••' : 'Enter password'}
                  className="h-12 rounded-2xl"
                  data-testid="staff-password-input"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="uppercase text-xs font-bold text-muted-foreground">
                  Active
                </Label>
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => handleDialogClose(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveStaff}
                  className="flex-1 btn-volt"
                  data-testid="save-staff-button"
                >
                  {editingStaff ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading staff members...</div>
      ) : staff.length === 0 ? (
        <Card className="p-8 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
          <p className="text-muted-foreground mb-4">No staff members yet</p>
          <Button onClick={() => setIsDialogOpen(true)} className="btn-volt">
            <Plus className="w-5 h-5 mr-2" />
            Add Your First Staff Member
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map(member => (
            <Card 
              key={member.id} 
              className={`p-6 ${!member.active ? 'opacity-60' : ''}`}
              data-testid="staff-card"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getRoleColor(member.role)}`}>
                    {getRoleIcon(member.role)}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{member.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getRoleColor(member.role)}`}>
                      {member.role}
                    </span>
                  </div>
                </div>
                {!member.active && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    Inactive
                  </span>
                )}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="truncate">{member.email}</span>
                </div>
                {member.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{member.phone}</span>
                  </div>
                )}
                {member.created_at && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>Added {formatDate(member.created_at)}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditStaff(member)}
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteStaff(member.id, member.name)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
